import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import Feather from '@react-native-vector-icons/feather/static';
import { GTFonts, useTheme } from '../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../utils/libs';
import type { AssignableStaffMember } from '../../api/employees';
import PersonAvatar from './PersonAvatar';

export interface StaffAssignModel {
  members: AssignableStaffMember[];
  loading: boolean;
  error: string | null;
  assignedStaffId: string | null;
  /** Assigns, or unassigns when the member is already assigned. */
  onToggle: (id: string) => void;
  onRetry: () => void;
  /** A waiter: orders are theirs, so the strip becomes an "Assigned to you" note. */
  selfAssignedName?: string | null;
}

interface StaffAssignSectionProps extends StaffAssignModel {
  /** Opens the full staff page. */
  onOpenAll: () => void;
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

/** "Assign staff · Optional" with a strip of avatars; tap one to assign. */
const StaffAssignSection: React.FC<StaffAssignSectionProps> = ({
  members,
  loading,
  error,
  assignedStaffId,
  onToggle,
  onRetry,
  selfAssignedName,
  onOpenAll,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  let body: React.ReactNode;
  if (selfAssignedName) {
    body = (
      <View style={styles.selfCard}>
        <PersonAvatar name={selfAssignedName} size={34} />
        <View style={styles.selfText}>
          <Text style={styles.selfTitle}>Assigned to you</Text>
          <Text style={styles.selfBody} numberOfLines={2}>
            Orders you save are assigned to {selfAssignedName}
          </Text>
        </View>
      </View>
    );
  } else if (loading) {
    body = (
      <View style={styles.strip} accessibilityLabel="Loading staff">
        {[0, 1, 2, 3, 4].map(index => (
          <View key={index} style={styles.chip}>
            <View style={styles.skeletonCircle} />
            <View style={styles.skeletonLine} />
          </View>
        ))}
      </View>
    );
  } else if (error) {
    body = (
      <View style={styles.emptyCard}>
        <Feather
          name="wifi-off"
          size={iconSize(18)}
          color={theme.colors.textGray600}
        />
        <Text style={styles.emptyText}>Couldn’t load staff</Text>
        <Pressable
          onPress={onRetry}
          accessibilityRole="button"
          style={styles.retry}>
          <Text style={styles.retryText}>Retry</Text>
        </Pressable>
      </View>
    );
  } else {
    body = (
      <BottomSheetScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={styles.strip}>
        {members.map(member => {
          const selected = assignedStaffId === member.id;
          return (
            <Pressable
              key={member.id}
              onPress={() => onToggle(member.id)}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={`${member.name}, ${member.role}${selected ? ', assigned' : ''}`}
              style={({ pressed }) => [styles.chip, pressed && styles.pressed]}>
              <PersonAvatar name={member.name} size={40} selected={selected} />
              <Text
                style={[styles.chipName, selected && styles.chipNameSelected]}
                numberOfLines={1}>
                {firstName(member.name)}
              </Text>
            </Pressable>
          );
        })}
      </BottomSheetScrollView>
    );
  }

  const showAll = !selfAssignedName && !loading && !error && members.length > 0;

  return (
    <View style={styles.section}>
      <View style={styles.head}>
        <View style={styles.headTitle}>
          <Text style={styles.title}>Assign staff</Text>
          {!selfAssignedName ? (
            <Text style={styles.optional}>Optional</Text>
          ) : null}
        </View>
        {showAll ? (
          <Pressable
            onPress={onOpenAll}
            accessibilityRole="button"
            accessibilityLabel={`All staff, ${members.length}`}
            hitSlop={{ top: 12, bottom: 12 }}
            style={({ pressed }) => [
              styles.allLink,
              pressed && styles.pressed,
            ]}>
            <Text style={styles.allLinkText}>All staff ({members.length})</Text>
            <Feather
              name="chevron-right"
              size={iconSize(16)}
              color={theme.colors.primaryDark}
            />
          </Pressable>
        ) : null}
      </View>
      {body}
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    section: {
      gap: v(8),
    },
    head: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s(8),
    },
    headTitle: {
      flexDirection: 'row',
      alignItems: 'baseline',
      gap: s(6),
    },
    title: {
      fontFamily: GTFonts.bold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    optional: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(15),
      color: theme.colors.textGray500,
    },
    allLink: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(2),
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
    allLinkText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.primaryDark,
    },
    strip: {
      flexDirection: 'row',
      gap: s(14),
      paddingTop: v(2),
      paddingBottom: v(4),
      paddingHorizontal: s(2),
    },
    chip: {
      width: s(54),
      alignItems: 'center',
      gap: v(5),
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
    chipName: {
      maxWidth: s(56),
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(14),
      color: theme.colors.textGray600,
    },
    chipNameSelected: {
      fontFamily: GTFonts.bold,
      color: theme.colors.primaryDark,
    },
    skeletonCircle: {
      width: s(40),
      height: s(40),
      borderRadius: s(20),
      backgroundColor: theme.colors.border,
    },
    skeletonLine: {
      width: s(36),
      height: v(9),
      borderRadius: s(5),
      backgroundColor: theme.colors.border,
    },
    emptyCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
      paddingVertical: v(12),
      paddingHorizontal: s(14),
      borderRadius: s(14),
      borderWidth: borderWidth(1),
      borderStyle: 'dashed',
      borderColor: theme.colors.borderGray200,
      backgroundColor: theme.colors.backgroundGray50,
    },
    emptyText: {
      flex: 1,
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textGray600,
    },
    retry: {
      height: v(32),
      paddingHorizontal: s(12),
      borderRadius: s(10),
      backgroundColor: theme.colors.primarySurface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    retryText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.primaryDark,
    },
    selfCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
      paddingVertical: v(10),
      paddingHorizontal: s(12),
      borderRadius: s(14),
      borderWidth: borderWidth(1),
      borderColor: theme.colors.infoLight,
      backgroundColor: theme.colors.primarySurface,
    },
    selfText: {
      flex: 1,
      minWidth: 0,
    },
    selfTitle: {
      fontFamily: GTFonts.bold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    selfBody: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textGray600,
    },
    pressed: {
      opacity: 0.75,
    },
  });

export default StaffAssignSection;
